import { useState } from 'react';
import { formatDate } from '../../lib/format';
import DateTimePicker from '../DateTimePicker';
import Modal from '../Modal';
import { Button } from '../ui';

const HOUR_MS = 3600_000;
const RACCOURCIS = [4, 24, 48] as const;

/**
 * Choix de la date de délivrance différée : calendrier + raccourcis H-4 / H-24 / H-48 avant la séance.
 * H-4 est le défaut de l'app quand aucune date n'est envoyée (guide §7.1) : on valide alors `null`,
 * exactement comme Trium, plutôt qu'une date explicite.
 */
export default function DateDelivranceModal({
  open,
  dateSeance,
  initial,
  today,
  busy,
  onClose,
  onValidate,
}: {
  open: boolean;
  dateSeance: string;
  /** Date de dévoilage actuelle (ISO) ; absente = H-4. */
  initial: string | null;
  today: Date;
  busy: boolean;
  onClose: () => void;
  /** `null` = repli de l'app à séance − 4 h. */
  onValidate: (date: string | null) => void;
}) {
  const seance = new Date(dateSeance).getTime();
  const [date, setDate] = useState(() => (initial ? new Date(initial) : new Date(seance - 4 * HOUR_MS)));
  const raccourci = RACCOURCIS.find((h) => date.getTime() === seance - h * HOUR_MS) ?? null;

  return (
    <Modal
      open={open}
      title="Date de délivrance des QR codes"
      onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => onValidate(raccourci === 4 ? null : date.toISOString())}
          >
            Valider
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <DateTimePicker value={date} onChange={setDate} today={today} />

        <div className="flex flex-wrap gap-2" role="group" aria-label="Raccourcis avant la séance">
          {RACCOURCIS.map((h) => (
            <button
              key={h}
              type="button"
              aria-pressed={raccourci === h}
              onClick={() => setDate(new Date(seance - h * HOUR_MS))}
              className={`rounded-full border px-3 py-1 text-sm font-semibold transition-colors ${
                raccourci === h
                  ? 'border-primary bg-primary text-white'
                  : 'border-card-border text-on-surface-variant hover:bg-white/10'
              }`}
            >
              H-{h}
            </button>
          ))}
        </div>

        <p className="text-sm text-on-surface-variant">
          Délivrance le <span className="font-semibold text-white">{formatDate(date.toISOString())}</span>
          <span className="text-outline"> · séance le {formatDate(dateSeance)}</span>
        </p>
      </div>
    </Modal>
  );
}
